/* eslint-disable react/jsx-curly-newline */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Button,
  Tooltip,
  Row,
  Col,
  InputNumber,
  Checkbox,
  TimePicker,
  DatePicker,
  Radio,
  Select,
  Input,
  Alert,
} from 'antd';

import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import TextArea from 'antd/lib/input/TextArea';
import moment, { Moment } from 'moment';
import SessionContext from '@providers/Session/SessionContext';
import useUpload from '@components/DeviceActionModal/steps/Upload';
import useDebounce from '@utils/useDebounce';

import api from '@services/api';
import { IDeviceActionRequestPayload } from '../types';

type SendSmsDataType = {
  uploadedDevices: any;
  selectedDevices: any;
  setUploadedDevices: any;
  currentStep: number;
  actionKey: number;
};

export const useSendSms = ({
  uploadedDevices,
  selectedDevices,
  setUploadedDevices,
  currentStep,
  actionKey,
}: SendSmsDataType) => {
  const { t } = useTranslation('device-actions-modal');

  const [from, setFrom] = useState();
  const [flash, setFlash] = useState('normal');
  const [schedule, setSchedule] = useState('normal');
  const [specifyDate, setSpecifyDate] = useState('false');

  const [normalize, setNormalize] = useState('true');
  const [languageCode, setLanguageCode] = useState<string>('AUTODETECT');

  const [text, setText] = useState<string>('');
  const [validityPeriod, setValidityPeriod] = useState<number | null>();
  const [sendAt, setSendAt] = useState<Moment | null>();
  const [days, setDays] = useState<string[] | undefined>([]);
  const [fromDate, setFromDate] = useState<Moment | null>(null);
  const [toDate, setToDate] = useState<Moment | null>(null);

  const [typeOnUpload, setTypeOnUpload] = useState<string>('manual');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [variableValues, setVariableValues] = useState<any>({});
  const debouncedSearchTerm = useDebounce(text, 700);

  const { data: templatesData } = useSwr('/service-proxy/sms-templates');
  const { data: templateDataInfo } = useSwr(
    templateId ? `/service-proxy/sms-templates/${templateId}` : null,
  );
  const { data: variablesData } = useSwr(
    templateId ? `/service-proxy/sms-templates/${templateId}/variables` : null,
  );

  const sendDeviceAction = useCallback(
    (payload: IDeviceActionRequestPayload) => {
      return api.post('/service-proxy/sms-request', payload);
    },
    [],
  );
  const { userLogged } = useContext(SessionContext);
  const { theme } = useContext(SessionContext);

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey: 124,
    setUploadedDevices,
    variablesData,
  });

  const customerIdLogged = userLogged?.customerId;
  const { data: optionsData } = useSwr(
    actionKey === 124 ? `/service-proxy/sms-available-options` : null,
  );
  const textWithVariablesFormat = useCallback(
    (textToFormat: string) => {
      let textWithVariables = textToFormat;
      variablesData?.forEach((variable: string) => {
        const variableValue = variableValues[variable] || '';
        textWithVariables = textWithVariables?.replace(
          `{${variable}}`,
          variableValue,
        );
      });
      return textWithVariables;
    },
    [variableValues, variablesData],
  );

  const debouncedTemplateVariables = useDebounce(
    textWithVariablesFormat(templateDataInfo?.text),
    700,
  );

  const { data: previewData } = useSwr(
    (templateDataInfo?.text && templateId) ||
      (debouncedSearchTerm && debouncedSearchTerm !== '')
      ? `/service-proxy/sms/preview`
      : null,
    {
      params: {
        text:
          templateDataInfo?.text && templateId
            ? debouncedTemplateVariables
            : debouncedSearchTerm,
        ...(optionsData?.previewLanguageCode
          ? { languageCode }
          : { normalize }),
      },
    },
  );

  const { data: sendersData } = useSwr(`/service-proxy/sms-senders`, {
    params: {
      customerId: customerIdLogged,
    },
  });

  const sendersDataFilter = useMemo(
    () =>
      sendersData?.filter(
        (sender: any) => sender.customerId === customerIdLogged,
      ),
    [sendersData, customerIdLogged],
  );

  const onSubmit = useCallback(async () => {
    const payload: any = {};

    if (templateId && templateDataInfo) {
      payload.destinations =
        selectedDevices?.length > 0
          ? selectedDevices.map((device: any) => {
              return {
                msisdn: device.msisdn,
                templateVariables: variableValues,
              };
            })
          : uploadedDevices.map((device: any) => {
              return {
                msisdn: device.msisdn,
                templateVariables: variablesData.reduce(
                  (acc: any, variable: any) => {
                    acc[variable] = device[variable] || '';
                    return acc;
                  },
                  {},
                ),
              };
            });
      payload.text = null;
      payload.templateId = templateId;
    } else {
      payload.destinations =
        selectedDevices?.length > 0
          ? selectedDevices.map((device: any) => {
              return { msisdn: device.msisdn };
            })
          : uploadedDevices.map((device: any) => {
              return {
                msisdn: device.msisdn,
              };
            });
      payload.text = text;
    }

    payload.from = from;
    payload.flash = flash === 'flash';
    payload.action = undefined;

    if (validityPeriod || validityPeriod === 0) {
      payload.validityPeriod = validityPeriod > 0 ? validityPeriod * 60 : 0;
    }
    if (optionsData?.previewLanguageCode) {
      payload.languageCode = languageCode;
    } else {
      payload.normalize = normalize;
    }

    if (sendAt) {
      payload.sendAt = sendAt.toISOString();
    }
    if (days && days.length > 0) {
      payload.deliveryTimeWindow = {
        days,
        from: moment(fromDate).format('HH:mm'),
        to: moment(toDate).format('HH:mm'),
      };
    }
    await sendDeviceAction(payload);
  }, [
    days,
    flash,
    from,
    fromDate,
    selectedDevices,
    sendDeviceAction,
    sendAt,
    text,
    toDate,
    uploadedDevices,
    validityPeriod,
    variableValues,
    variablesData,
    templateId,
    templateDataInfo,
    optionsData,
    normalize,
    languageCode,
  ]);

  const reinitState = () => {
    setFrom(undefined);
    setFlash('normal');
    setSchedule('normal');
    setSpecifyDate('false');
    setText('');
    setValidityPeriod(null);
    setSendAt(null);
    setDays([]);
    setFromDate(null);
    setToDate(null);
    setTemplateId(null);
    setVariableValues({});
    uploadReinitState();
    setNormalize('true');
    setLanguageCode('AUTODETECT');
  };

  const updatedText = useMemo(() => {
    if (templateDataInfo) {
      let textWithVariables = templateDataInfo.text;
      variablesData?.forEach((variable: string) => {
        const variableValue = variableValues[variable] || '';
        textWithVariables = textWithVariables?.replace(
          `{${variable}}`,
          variableValue,
        );
      });
      return textWithVariables;
    }
    return text;
  }, [templateDataInfo, text, variableValues, variablesData]);

  const contentSteps = useMemo(() => {
    return [
      ...(selectedDevices?.length > 0
        ? [
            {
              title: t('steps.sms-send.first-step'),
              content: (
                <div style={{ padding: 16 }}>
                  <Row gutter={[16, 16]}>
                    {sendersDataFilter &&
                      sendersDataFilter.length > 0 &&
                      optionsData?.sender && (
                        <Col xl={24} xs={24}>
                          <span style={{ color: theme?.colors?.text1 }}>
                            {`${t('steps.sms-send.from')}`}
                          </span>

                          <Select
                            value={from}
                            onChange={valueToChange => setFrom(valueToChange)}
                            style={{ width: '100%' }}
                            allowClear
                          >
                            {sendersDataFilter?.map((sender: any) => (
                              <Select.Option
                                key={sender.id}
                                value={sender.sender}
                              >
                                {sender.sender}
                              </Select.Option>
                            ))}
                          </Select>
                        </Col>
                      )}
                    {optionsData?.flash && (
                      <Col xl={24} xs={24}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ color: theme?.colors?.text1 }}>
                            {`${t('steps.sms-send.type')}`}
                          </span>
                          <Tooltip title={t('steps.sms-send.flash-info')}>
                            <Button type="link">
                              <InfoCircleOutlined />
                            </Button>
                          </Tooltip>
                        </div>
                        <Radio.Group
                          onChange={event => setFlash(event.target.value)}
                          defaultValue="normal"
                          value={flash}
                        >
                          <Radio.Button value="normal">
                            {' '}
                            {t('steps.sms-send.normal')}
                          </Radio.Button>
                          <Radio.Button value="flash">
                            {' '}
                            {t('steps.sms-send.flash')}
                          </Radio.Button>
                        </Radio.Group>
                      </Col>
                    )}

                    <Col xl={24} xs={24}>
                      <span style={{ color: theme?.colors?.text1 }}>
                        {`${t('steps.sms-send.template')}`}
                      </span>

                      <Select
                        value={templateId}
                        onChange={value => setTemplateId(value)}
                        style={{ width: '100%' }}
                        allowClear
                      >
                        {templatesData?.map((template: any) => (
                          <Select.Option key={template.id} value={template.id}>
                            {template.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Col>

                    {variablesData?.length > 0 && (
                      <Col xl={24} xs={24}>
                        <span style={{ color: theme?.colors?.text1 }}>
                          {`${t('steps.sms-send.variables')}`}
                        </span>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gridGap: '8px',
                            padding: '8px',
                          }}
                        >
                          {variablesData?.map((variable: string) => (
                            <div>
                              <span style={{ color: theme?.colors?.text1 }}>
                                {variable}
                              </span>
                              <Input
                                key={variable}
                                value={variableValues[variable] || ''}
                                onChange={
                                  e =>
                                    setVariableValues({
                                      ...variableValues,
                                      [variable]: e.target.value,
                                    })
                                  // eslint-disable-next-line react/jsx-curly-newline
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </Col>
                    )}

                    <Col xl={24} xs={24}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          margin: '6px',
                          alignItems: 'end',
                        }}
                      >
                        <span style={{ color: theme?.colors?.text1 }}>
                          {`${t('steps.sms-send.text')}`}
                        </span>

                        {optionsData?.previewLanguageCode ? (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <div>
                              <span style={{ color: theme?.colors?.text1 }}>
                                {`${t('steps.sms-send.code')}`}
                              </span>
                              <Tooltip
                                title={t('steps.sms-send.language-code-info')}
                              >
                                <Button type="link">
                                  <InfoCircleOutlined />
                                </Button>
                              </Tooltip>
                            </div>
                            <Select
                              value={languageCode}
                              onChange={event => setLanguageCode(event)}
                              style={{ minWidth: '150px' }}
                            >
                              <Select.Option value="PT">PT</Select.Option>
                              <Select.Option value="TR">TR</Select.Option>
                              <Select.Option value="ES">ES</Select.Option>
                              <Select.Option value="AUTODETECT">
                                AUTODETECT
                              </Select.Option>
                            </Select>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <div>
                              <span style={{ color: theme?.colors?.text1 }}>
                                {`${t('steps.sms-send.code')}`}
                              </span>
                              <Tooltip
                                title={t('steps.sms-send.normalize-info')}
                              >
                                <Button type="link">
                                  <InfoCircleOutlined />
                                </Button>
                              </Tooltip>
                            </div>
                            <Select
                              value={normalize}
                              onChange={event => setNormalize(event)}
                              style={{ minWidth: '150px' }}
                            >
                              <Select.Option value="true">
                                Normalizar
                              </Select.Option>
                              <Select.Option value="false">
                                Não normalizar
                              </Select.Option>
                            </Select>
                          </div>
                        )}
                      </div>

                      {templateDataInfo ? (
                        <span
                          style={{
                            color: theme?.colors?.text1,
                            marginTop: '8px',
                          }}
                        >
                          {updatedText}
                        </span>
                      ) : (
                        <TextArea
                          onChange={({ target: { value } }) => setText(value)}
                          value={text}
                        />
                      )}

                      {previewData && (
                        <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>
                          <span style={{ color: theme?.colors?.text1 }}>
                            {`${t(
                              'steps.sms-send.previewData',
                            )} ${previewData}`}
                          </span>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ),
            },
          ]
        : [
            {
              title: t('steps.sms-send.select-type-step'),
              content: (
                <Row gutter={[16, 16]}>
                  <Col
                    xl={24}
                    xs={24}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Radio.Group
                      value={typeOnUpload}
                      onChange={event => {
                        setTypeOnUpload(event.target.value);
                        setTemplateId(null);
                        setText('');
                        uploadReinitState();
                      }}
                    >
                      <Radio.Button value="template">
                        {t('steps.sms-send.template')}
                      </Radio.Button>
                      <Radio.Button value="manual">
                        {t('steps.sms-send.manual')}
                      </Radio.Button>
                    </Radio.Group>
                  </Col>
                  {typeOnUpload === 'template' && (
                    <Col xl={24} xs={24}>
                      <span style={{ color: theme?.colors?.text1 }}>
                        {`${t('steps.sms-send.template')}`}
                      </span>

                      <Select
                        value={templateId}
                        onChange={value => setTemplateId(value)}
                        style={{ width: '100%' }}
                        allowClear
                      >
                        {templatesData?.map((template: any) => (
                          <Select.Option key={template.id} value={template.id}>
                            {template.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Col>
                  )}
                </Row>
              ),
            },
            uploadStep,
            {
              title: t('steps.sms-send.first-step'),
              content: (
                <div style={{ padding: 16 }}>
                  <Row gutter={[16, 16]}>
                    {sendersDataFilter &&
                      sendersDataFilter.length > 0 &&
                      optionsData?.sender && (
                        <Col xl={24} xs={24}>
                          <span style={{ color: theme?.colors?.text1 }}>
                            {`${t('steps.sms-send.from')}`}
                          </span>

                          <Select
                            value={from}
                            onChange={valueToChange => setFrom(valueToChange)}
                            style={{ width: '100%' }}
                            allowClear
                          >
                            {sendersDataFilter?.map((sender: any) => (
                              <Select.Option
                                key={sender.id}
                                value={sender.sender}
                              >
                                {sender.sender}
                              </Select.Option>
                            ))}
                          </Select>
                        </Col>
                      )}
                    {optionsData?.flash && (
                      <Col xl={24} xs={24}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ color: theme?.colors?.text1 }}>
                            {`${t('steps.sms-send.type')}`}
                          </span>
                          <Tooltip title={t('steps.sms-send.flash-info')}>
                            <Button type="link">
                              <InfoCircleOutlined />
                            </Button>
                          </Tooltip>
                        </div>
                        <Radio.Group
                          onChange={event => setFlash(event.target.value)}
                          defaultValue="normal"
                          value={flash}
                        >
                          <Radio.Button value="normal">
                            {' '}
                            {t('steps.sms-send.normal')}
                          </Radio.Button>
                          <Radio.Button value="flash">
                            {' '}
                            {t('steps.sms-send.flash')}
                          </Radio.Button>
                        </Radio.Group>
                      </Col>
                    )}
                    {typeOnUpload === 'manual' ? (
                      <>
                        <Col xl={24} xs={24}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              margin: '6px',
                              alignItems: 'end',
                            }}
                          >
                            <span style={{ color: theme?.colors?.text1 }}>
                              {`${t('steps.sms-send.text')}`}
                            </span>

                            {optionsData?.previewLanguageCode ? (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <div>
                                  <span style={{ color: theme?.colors?.text1 }}>
                                    {`${t('steps.sms-send.code')}`}
                                  </span>
                                  <Tooltip
                                    title={t(
                                      'steps.sms-send.language-code-info',
                                    )}
                                  >
                                    <Button type="link">
                                      <InfoCircleOutlined />
                                    </Button>
                                  </Tooltip>
                                </div>
                                <Select
                                  value={languageCode}
                                  onChange={event => setLanguageCode(event)}
                                  style={{ minWidth: '150px' }}
                                >
                                  <Select.Option value="PT">PT</Select.Option>
                                  <Select.Option value="TR">TR</Select.Option>
                                  <Select.Option value="ES">ES</Select.Option>
                                  <Select.Option value="AUTODETECT">
                                    AUTODETECT
                                  </Select.Option>
                                </Select>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <div>
                                  <span style={{ color: theme?.colors?.text1 }}>
                                    {`${t('steps.sms-send.code')}`}
                                  </span>
                                  <Tooltip
                                    title={t('steps.sms-send.normalize-info')}
                                  >
                                    <Button type="link">
                                      <InfoCircleOutlined />
                                    </Button>
                                  </Tooltip>
                                </div>
                                <Select
                                  value={normalize}
                                  onChange={event => setNormalize(event)}
                                  style={{ minWidth: '150px' }}
                                >
                                  <Select.Option value="true">
                                    Normalizar
                                  </Select.Option>
                                  <Select.Option value="false">
                                    Não normalizar
                                  </Select.Option>
                                </Select>
                              </div>
                            )}
                          </div>

                          <TextArea
                            onChange={({ target: { value } }) => setText(value)}
                            value={text}
                          />
                          {previewData && (
                            <div
                              style={{ marginTop: '8px', fontSize: '0.8rem' }}
                            >
                              <span style={{ color: theme?.colors?.text1 }}>
                                {`${t(
                                  'steps.sms-send.previewData',
                                )} ${previewData}`}
                              </span>
                            </div>
                          )}
                        </Col>
                      </>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            margin: '6px',
                          }}
                        >
                          <div>
                            <span style={{ color: theme?.colors?.text1 }}>
                              {`${t('steps.sms-send.code')}`}
                            </span>
                            <Tooltip
                              title={t('steps.sms-send.language-code-info')}
                            >
                              <Button type="link">
                                <InfoCircleOutlined />
                              </Button>
                            </Tooltip>
                          </div>

                          {optionsData?.previewLanguageCode ? (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Select
                                value={languageCode}
                                onChange={event => setLanguageCode(event)}
                                style={{ minWidth: '150px' }}
                              >
                                <Select.Option value="PT">PT</Select.Option>
                                <Select.Option value="TR">TR</Select.Option>
                                <Select.Option value="ES">ES</Select.Option>
                                <Select.Option value="AUTODETECT">
                                  AUTODETECT
                                </Select.Option>
                              </Select>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Select
                                value={normalize}
                                onChange={event => setNormalize(event)}
                                style={{ minWidth: '150px' }}
                              >
                                <Select.Option value="true">
                                  Normalizar
                                </Select.Option>
                                <Select.Option value="false">
                                  Não normalizar
                                </Select.Option>
                              </Select>
                            </div>
                          )}
                        </div>

                        {previewData && (
                          <div
                            style={{
                              marginTop: '8px',
                              fontSize: '0.8rem',
                              marginBottom: '5px',
                            }}
                          >
                            <span style={{ color: theme?.colors?.text1 }}>
                              {`${t(
                                'steps.sms-send.previewData',
                              )} ${previewData}`}
                            </span>
                          </div>
                        )}

                        <Alert
                          message={t('steps.sms-send.template-alert')}
                          description={t(
                            'steps.sms-send.template-alert-description',
                          )}
                          type="warning"
                          showIcon
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}
                  </Row>
                </div>
              ),
            },
          ]),

      ...(optionsData?.scheduled
        ? [
            {
              title: t('steps.sms-send.second-step'),
              content: (
                <div style={{ padding: 16 }}>
                  <Row gutter={[16, 16]}>
                    {optionsData?.scheduled && (
                      <Col xl={24} xs={24}>
                        <span style={{ color: theme?.colors?.text1 }}>
                          {`${t('steps.sms-send.typeDate')}`}
                        </span>
                        <div
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <Radio.Group
                            onChange={event => {
                              setDays([]);
                              setValidityPeriod(null);
                              setFromDate(null);
                              setToDate(null);
                              setSendAt(undefined);
                              setSchedule(event.target.value);
                              setSpecifyDate('false');
                            }}
                            value={schedule}
                          >
                            <Radio.Button value="normal">
                              {t('steps.sms-send.normal')}
                            </Radio.Button>
                            <Radio.Button value="schedule">
                              {t('steps.sms-send.schedule')}
                            </Radio.Button>
                          </Radio.Group>
                        </div>
                      </Col>
                    )}

                    {schedule === 'normal' ? (
                      <>
                        <Col xl={24} xs={24}>
                          <span
                            style={{
                              color: theme?.colors?.text1,
                              marginRight: '5px',
                            }}
                          >
                            {`${t('steps.sms-send.specidyDate')}`}
                          </span>

                          <Radio.Group
                            defaultValue="false"
                            value={specifyDate}
                            onChange={event => {
                              setDays([]);
                              setValidityPeriod(undefined);
                              setFromDate(null);
                              setToDate(null);
                              setSendAt(undefined);
                              setSpecifyDate(event.target.value);
                            }}
                          >
                            <Radio.Button value="false">
                              {t('steps.sms-send.no')}
                            </Radio.Button>
                            <Radio.Button value="true">
                              {t('steps.sms-send.yes')}
                            </Radio.Button>
                          </Radio.Group>
                        </Col>
                        {specifyDate === 'true' && (
                          <>
                            {optionsData?.deliveryTimeWindow && (
                              <Col xl={24} xs={24}>
                                <span style={{ color: theme?.colors?.text1 }}>
                                  {`${t('steps.sms-send.deliveryTimeWindow')}`}
                                </span>

                                <Checkbox.Group
                                  options={[
                                    'monday',
                                    'tuesday',
                                    'wednesday',
                                    'thursday',
                                    'friday',
                                    'saturday',
                                    'sunday',
                                  ].map(day => ({
                                    label: t(`steps.sms-send.options.${day}`),
                                    value: day.toUpperCase(),
                                  }))}
                                  value={days}
                                  onChange={(daysToChange: any) =>
                                    setDays(daysToChange)
                                  }
                                />
                              </Col>
                            )}

                            {days && days?.length > 0 && (
                              <>
                                <Col xl={24} xs={24}>
                                  <span style={{ color: theme?.colors?.text1 }}>
                                    {`${t('steps.sms-send.hoursInterval')}`}
                                  </span>
                                </Col>
                                <Col xl={12} xs={24}>
                                  <span
                                    style={{
                                      color: theme?.colors?.text1,
                                      marginRight: '5px',
                                    }}
                                  >
                                    {`${t('steps.sms-send.fromDate')}`}
                                  </span>
                                  <TimePicker
                                    value={fromDate}
                                    format="HH:mm"
                                    onChange={timeChange =>
                                      setFromDate(timeChange)
                                    }
                                  />
                                </Col>

                                <Col xl={12} xs={24}>
                                  <span
                                    style={{
                                      color: theme?.colors?.text1,
                                      marginRight: '5px',
                                    }}
                                  >
                                    {`${t('steps.sms-send.toDate')}`}
                                  </span>
                                  <TimePicker
                                    value={toDate}
                                    format="HH:mm"
                                    onChange={timeChange =>
                                      setToDate(timeChange)
                                    }
                                  />
                                </Col>
                              </>
                            )}
                            <Col xl={24} xs={24}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <span style={{ color: theme?.colors?.text1 }}>
                                  {`${t('steps.sms-send.validityPeriod')}`}
                                </span>
                                <Tooltip
                                  title={t(
                                    'steps.sms-send.validityPeriod-info',
                                  )}
                                >
                                  <Button type="link">
                                    <InfoCircleOutlined />
                                  </Button>
                                </Tooltip>
                              </div>

                              <InputNumber
                                value={validityPeriod}
                                onChange={value => setValidityPeriod(value)}
                                max={48}
                                style={{
                                  width: '150px',
                                }}
                              />
                            </Col>
                          </>
                        )}
                      </>
                    ) : (
                      <Col xl={12} xs={24}>
                        <span style={{ color: theme?.colors?.text1 }}>
                          {`${t('steps.sms-send.sendAt')}`}
                          <span style={{ color: 'red' }}>*</span>
                        </span>
                        <DatePicker
                          value={sendAt}
                          showTime={{ format: 'HH:mm' }}
                          onChange={date => setSendAt(date)}
                        />
                      </Col>
                    )}
                  </Row>
                </div>
              ),
            },
          ]
        : []),
    ];
  }, [
    t,
    days,
    schedule,
    text,
    flash,
    sendersDataFilter,
    specifyDate,
    from,
    validityPeriod,
    sendAt,
    fromDate,
    toDate,
    optionsData,
    templateDataInfo,
    templatesData,
    templateId,
    variableValues,
    variablesData,
    updatedText,
    languageCode,
    normalize,
    previewData,
    uploadStep,
    selectedDevices,
    typeOnUpload,
    uploadReinitState,
    theme,
  ]);

  const nextStepIsDisabled = useCallback(() => {
    if (!selectedDevices || selectedDevices?.length === 0) {
      if (currentStep === 0) {
        return typeOnUpload === 'manual' ? false : !templateId;
      }
      if (currentStep === 1) {
        return !uploadedDevices || uploadedDevices?.length === 0;
      }
      if (currentStep === 2 && typeOnUpload === 'manual') {
        return !text;
      }
      if (currentStep === 3) {
        if (schedule === 'schedule') {
          return !sendAt;
        }
      }
      return false;
    }

    if (currentStep === 0) {
      if (!templateId) {
        return !text;
      }
      if (variablesData?.length === 0) {
        return false;
      }
      const areVariableValuesValid =
        variableValues &&
        Object.keys(variableValues).length > 0 &&
        Object.values(variableValues).every(
          value => value !== null && value !== undefined && value !== '',
        );
      return !areVariableValuesValid;
    }
    if (currentStep === 1) {
      if (schedule === 'schedule') {
        return !sendAt;
      }
    }
    return false;
  }, [
    selectedDevices,
    currentStep,
    typeOnUpload,
    templateId,
    uploadedDevices,
    text,
    schedule,
    sendAt,
    variablesData?.length,
    variableValues,
  ]);

  const sendSmsData = {
    contentSteps,
    reinitState,
    onSubmit,
    nextStepIsDisabled,
  };

  return {
    sendSmsData,
  };
};
